/**
 * Business Scaling Assessment — loader + scoring (from the sister-site hand-off kit).
 * Content resilience: live feed -> last cached copy -> bundled snapshot. Scoring is
 * 100% client-side. Never POST answers to the content provider.
 */
const DEFAULT_LIVE_URL = "https://scaledeasy.com/api/public/assessment";
const DEFAULT_CACHE_KEY = "cvc.scaledeasy.assessment.v1";
const DEFAULT_TIMEOUT_MS = 3000;

function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readCache(cacheKey) {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(cacheKey, data) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch {
    /* quota / private mode — ignore, snapshot still works */
  }
}

function looksValid(a) {
  return (
    a &&
    typeof a === "object" &&
    Array.isArray(a.pillars) &&
    a.pillars.length > 0 &&
    a.scoring &&
    Array.isArray(a.answer_options)
  );
}

/** live feed (short timeout, refreshes cache) -> cached copy -> bundled snapshot. Never throws with a snapshot. */
export async function loadAssessment(options = {}) {
  const {
    snapshot = null,
    liveUrl = DEFAULT_LIVE_URL,
    cacheKey = DEFAULT_CACHE_KEY,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    disableLive = false,
  } = options;
  if (!disableLive && typeof fetch === "function") {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(liveUrl, { signal: controller.signal, credentials: "omit" });
      clearTimeout(timer);
      if (res.ok) {
        const live = await res.json();
        if (looksValid(live)) {
          writeCache(cacheKey, live);
          return live;
        }
      }
    } catch {
      /* offline / timeout / CORS — fall through to cache + snapshot */
    }
  }
  const cached = readCache(cacheKey);
  if (looksValid(cached)) return cached;
  if (looksValid(snapshot)) return snapshot;
  throw new Error("No assessment available — pass a bundled `snapshot` (assessment.json) to loadAssessment().");
}

/** pillars -> groups -> items flattened with deterministic ids (`${group.id}#${i}`). */
export function flattenStatements(assessment) {
  const out = [];
  for (const pillar of assessment.pillars || []) {
    for (const group of pillar.groups || []) {
      (group.items || []).forEach((text, i) => {
        out.push({
          id: `${group.id}#${i}`,
          pillarId: pillar.id,
          pillarLabel: pillar.label,
          groupId: group.id,
          groupTitle: group.title,
          indexInGroup: i,
          text,
        });
      });
    }
  }
  return out;
}

function pct(sum, count) {
  if (!count) return 0;
  return Math.round((sum / (count * 2)) * 100);
}

function verdictFor(bands, overallPct) {
  const sorted = [...(bands || [])].sort((a, b) => b.min_pct - a.min_pct);
  for (const b of sorted) {
    if (overallPct >= b.min_pct) return b.label;
  }
  return sorted.length ? sorted[sorted.length - 1].label : "";
}

/** answers = { [statementId]: 0 | 1 | 2 }; missing answers count as 0. */
export function scoreAssessment(assessment, answers = {}) {
  const statements = flattenStatements(assessment);
  const total = statements.length;
  const groupAgg = new Map();
  const pillarAgg = new Map();
  let overallSum = 0;
  let answeredCount = 0;
  for (const s of statements) {
    const raw = answers[s.id];
    const v = raw === 1 || raw === 2 ? raw : 0;
    if (raw === 0 || raw === 1 || raw === 2) answeredCount += 1;
    overallSum += v;
    if (!groupAgg.has(s.groupId))
      groupAgg.set(s.groupId, { id: s.groupId, title: s.groupTitle, pillarId: s.pillarId, sum: 0, count: 0 });
    const g = groupAgg.get(s.groupId);
    g.sum += v;
    g.count += 1;
    if (!pillarAgg.has(s.pillarId))
      pillarAgg.set(s.pillarId, { id: s.pillarId, label: s.pillarLabel, sum: 0, count: 0 });
    const p = pillarAgg.get(s.pillarId);
    p.sum += v;
    p.count += 1;
  }
  const groups = [...groupAgg.values()].map((g) => ({ id: g.id, title: g.title, pillarId: g.pillarId, pct: pct(g.sum, g.count) }));
  const pillars = [...pillarAgg.values()].map((p) => ({ id: p.id, label: p.label, pct: pct(p.sum, p.count) }));
  const overallPct = pct(overallSum, total);
  const weakestCount = (assessment.scoring && assessment.scoring.weakest_areas_count) || 3;
  const weakestGroups = [...groups].sort((a, b) => a.pct - b.pct).slice(0, weakestCount);
  return {
    overallPct,
    verdict: verdictFor(assessment.scoring && assessment.scoring.verdict_bands, overallPct),
    answeredCount,
    totalCount: total,
    complete: answeredCount === total,
    pillars,
    groups,
    weakestGroups,
  };
}

/** Optional 3-question "scaling test" (one per pillar). */
export function scoreScalingTest(assessment, answers = {}) {
  const items = assessment.scaling_test || [];
  let sum = 0;
  for (const item of items) {
    const raw = answers[item.pillar];
    sum += raw === 1 || raw === 2 ? raw : 0;
  }
  const p = pct(sum, items.length);
  return { pct: p, verdict: verdictFor(assessment.scoring && assessment.scoring.scaling_test_bands, p) };
}

export const SCALEDEASY_ASSESSMENT_LIVE_URL = DEFAULT_LIVE_URL;
